import {
  Component,
  ChangeDetectionStrategy,
  Input,
  Output,
  EventEmitter,
} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule } from "@angular/forms";
import { TranslateModule } from "@ngx-translate/core";
import { Account } from "../../../../api/models/account";
import { CompanyAccount } from "../../../../api/models/company-account";
import { TypeaheadComponent } from "../../../../core/typeahead/typeahead.component";

/**
 * Standalone component for managing company accounts table.
 * Handles add/remove/primary toggle functionality.
 *
 * Usage:
 * ```html
 * <app-company-accounts-table
 *   [accounts]="companyAccounts()"
 *   (accountsChange)="companyAccounts.set($event)"
 * ></app-company-accounts-table>
 * ```
 */
@Component({
  selector: "app-company-accounts-table",
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, TypeaheadComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <h3>{{ "Accounts" | translate }}</h3>

    <div class="accounts">
      <div class="btn-toolbar mb-3">
        <div class="input-group d-flex align-items-center" style="gap: 5px">
          <div
            app-typeahead
            (selectEvent)="handleAccountSelect($event)"
            [selectorName]="'emailAddress'"
            [apiEndpoint]="'account'"
          ></div>
          <div>
            <label>
              <input
                type="checkbox"
                [(ngModel)]="newAccountIsPrimary"
              />
              {{ "Is primary contact" | translate }}?
            </label>
          </div>

          <div class="input-group-append">
            <button class="btn btn-primary ml-auto" (click)="addAccount()">
              {{ "Add" | translate }}
            </button>
          </div>
        </div>
      </div>

      <table class="table table-bordered">
        <thead>
          <tr>
            <th>{{ "Email address" | translate }}</th>
            <th>{{ "First name" | translate }}</th>
            <th>{{ "Last name" | translate }}</th>
            <th>{{ "Primary contact" | translate }}</th>
            <th></th>
          </tr>
        </thead>
        @if (accounts.length > 0) {
          <tbody>
            @for (
              account of accounts;
              track account.accountId;
              let key = $index
            ) {
              <tr class="account">
                <td class="form-group">
                  {{ account.account?.emailAddress }}
                </td>
                <td class="form-group">
                  {{ account.account?.firstName }}
                </td>
                <td class="form-group">
                  {{ account.account?.lastName }}
                </td>
                <td class="form-group">
                  {{ (account.isPrimaryContact ? "Yes" : "No") | translate }}

                  @if (!account.isPrimaryContact) {
                    <div>
                      <button
                        class="btn btn-primary"
                        (click)="makePrimary(account, true)"
                      >
                        {{ "Make primary" | translate }}
                      </button>
                    </div>
                  } @else {
                    <div>
                      <button
                        class="btn btn-primary"
                        (click)="makePrimary(account, false)"
                      >
                        {{ "Undo primary" | translate }}
                      </button>
                    </div>
                  }
                </td>
                <td>
                  <button class="btn btn-danger" (click)="deleteAccount(key)">
                    {{ "Delete" | translate }}
                  </button>
                </td>
              </tr>
            }
          </tbody>
        }
      </table>
    </div>
  `,
})
export class CompanyAccountsTableComponent {
  @Input() accounts: CompanyAccount[] = [];
  @Output() accountsChange = new EventEmitter<CompanyAccount[]>();

  newAccountIsPrimary = false;
  private selectedAccount: { data?: { id: string } } | null = null;

  handleAccountSelect(event: { data?: { id: string } }): void {
    this.selectedAccount = event;
  }

  addAccount(): void {
    if (!this.selectedAccount?.data?.id) return;

    const newAccount: CompanyAccount = {
      account: this.selectedAccount.data as Account,
      accountId: this.selectedAccount.data.id,
      isPrimaryContact: this.newAccountIsPrimary,
    };

    const updated = [...this.accounts, newAccount];
    this.accountsChange.emit(updated);

    // Reset
    this.selectedAccount = null;
    this.newAccountIsPrimary = false;
  }

  makePrimary(account: CompanyAccount, isPrimary: boolean): void {
    const updated = this.accounts.map((a) =>
      a.accountId === account.accountId
        ? { ...a, isPrimaryContact: isPrimary }
        : a
    );
    this.accountsChange.emit(updated);
  }

  deleteAccount(index: number): void {
    const updated = this.accounts.filter((_, i) => i !== index);
    this.accountsChange.emit(updated);
  }
}
